import styles from '@ui/components/InputSlider/InputSlider.module.scss';
import Tooltip from '@ui/components/Tooltip/Tooltip';
import Content from '@ui/components/Content/Content';
import ReactSlider from 'react-slider';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import React, { useState } from 'react';

const InputSlider = ({
  afterSlideChange,
  ariaLabel = null,
  axisLabels,
  disabled = false,
  hasProgression = false,
  max = 100,
  min = 0,
  onSlideChange,
  step = 1,
  thumb = 'dot',
  thumbSize = 'default',
  tooltipProps,
  tooltipText = null,
  value = 50,
  ...props
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  return (
    <div className={styles.container}>
      <ReactSlider
        ariaLabel={ariaLabel}
        className={styles.slider}
        disabled={disabled}
        max={max}
        min={min}
        onAfterChange={afterSlideChange || function () {}}
        onChange={onSlideChange}
        renderThumb={thumbProps => (
          <div
            onPointerDown={() => setIsTooltipOpen(true)}
            onPointerEnter={() => setIsTooltipOpen(true)}
            onPointerMove={() => setIsTooltipOpen(true)}
            onTouchCancel={() => setIsTooltipOpen(false)}
            {...thumbProps}
            onBlur={() => setIsTooltipOpen(false)}
            onFocus={() => {
              setIsTooltipOpen(true);
              // react-slider has its own onFocus handler, so we need to call
              // it here again explicitly
              thumbProps.onFocus();
            }}
          >
            {tooltipText && (
              <Tooltip
                isControlled
                isOpen={isTooltipOpen}
                setIsOpen={setIsTooltipOpen}
                text={tooltipText}
                {...(tooltipProps || {})}
              >
                <div className={styles[thumbSize]} />
              </Tooltip>
            )}
          </div>
        )}
        step={step}
        thumbClassName={classNames(
          styles.thumb,
          styles[thumb],
          styles[thumbSize],
          {
            [styles.disabled]: disabled,
          },
        )}
        trackClassName={classNames(styles.track, {
          [styles.disabled]: disabled,
          [styles.progression]: hasProgression,
        })}
        value={value}
        {...props}
      />
      {Array.isArray(axisLabels) && axisLabels?.length > 0 && (
        // axis labels provide no additional benefit for screen readers users
        // to the input range aria attributes and are therefore hidden from
        // them.
        <div aria-hidden className={styles.axisLabelsContainer}>
          {axisLabels.map(label => (
            <Content className={styles.axisLabel} key={`axisLabels-${label}`}>
              {label}
            </Content>
          ))}
        </div>
      )}
    </div>
  );
};

InputSlider.propTypes = {
  afterSlideChange: PropTypes.func,
  ariaLabel: PropTypes.string,
  axisLabels: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  hasProgression: PropTypes.bool,
  max: PropTypes.number,
  min: PropTypes.number,
  step: PropTypes.number,
  thumb: PropTypes.oneOf([
    'dot',
    'indexDown',
    'smiley',
    'thumbsDown',
    'thumbsUp',
  ]),
  thumbSize: PropTypes.oneOf(['default', 'md', 'lg', 'xl', 'xxl']),
  // eslint-disable-next-line react/forbid-prop-types
  tooltipProps: PropTypes.object,
  tooltipText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  value: PropTypes.number,
};

export default InputSlider;
